import { red } from '@carbon/colors';
import { FormRowStatus } from 'atoms/create-resource-atom';
import { Map } from 'components/map';
import { createBoundingBoxAtom } from 'components/map/atoms';
import { QuarteredPolygon } from 'components/map/quartered-polygon';
import { MapUserConfigAtom } from 'components/map/types';
import { useCalculateMapDimensions } from 'components/map/utils';
import { Atom, useAtomValue } from 'jotai';
import { uniqBy } from 'lodash-es';
import { POLYGON_SELECTED_COLOR } from 'lib/map';
import { ReactNode, useMemo, useRef } from 'react';
import type { MapRef } from 'react-map-gl';
import { ReferenceLocationResponse } from 'types/api-responses';

import style from './development-area-map.module.scss';
import { resourcesAtom } from 'atoms/root';

interface Props {
  referenceLocationIds: number[];
  geographies: {
    id: number | null;
    referenceLocation: ReferenceLocationResponse;
    quarterCalls: string[];
    status?: FormRowStatus;
    /** Unique layer key, for geographies that share a reference location. */
    key?: string;
    /** Drawn in the selected color. */
    highlighted?: boolean;
  }[];
  mapConfigAtom: MapUserConfigAtom;
  /** A dedicated ref, so a page can move this map (fitBounds, flyTo). */
  refAtom?: Atom<MapRef | null>;
  /** Extra layers drawn above the geographies. */
  children?: ReactNode;
}

const polygonColor = (status?: FormRowStatus, highlighted?: boolean) => {
  if (highlighted) return POLYGON_SELECTED_COLOR;
  return status === 'trash' ? red[70] : undefined;
};

const DevelopmentAreaMap = ({
  referenceLocationIds,
  geographies,
  mapConfigAtom,
  refAtom,
  children,
}: Props) => {
  const containerRef = useRef(null);
  const resources = useAtomValue(resourcesAtom);
  const idsMemoString = referenceLocationIds.join('-');
  const boundingBoxAtom = useMemo(
    () =>
      createBoundingBoxAtom(
        referenceLocationIds,
        resources?.boundingBoxes.href
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsMemoString, resources?.boundingBoxes.href]
  );
  const boundingBox = useAtomValue(boundingBoxAtom);
  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: boundingBox.length ? boundingBox : undefined,
    containerRef,
  });

  return (
    <div ref={containerRef} className={style.container}>
      <Map
        className={style.map}
        longitude={longitude}
        latitude={latitude}
        zoom={zoom - 1}
        geographies={uniqBy(geographies, (el) => el.referenceLocation.id).map(
          (el) => ({
            id: el.referenceLocation.id,
            name: el.referenceLocation.name,
            quarterCalls: el.quarterCalls,
          })
        )}
        mapConfigAtom={mapConfigAtom}
        refAtom={refAtom}
        showControls
        showMapFilters
      >
        {geographies.map(
          (
            { referenceLocation, quarterCalls, status, key, highlighted },
            i
          ) => {
            return (
              <QuarteredPolygon
                location={referenceLocation}
                quarterCalls={quarterCalls}
                id={referenceLocation.id}
                layerKey={key}
                key={key ?? `${referenceLocation.id}-${i}`}
                color={polygonColor(status, highlighted)}
              />
            );
          }
        )}
        {children}
      </Map>
    </div>
  );
};

export { DevelopmentAreaMap };
